import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';

const COLORS = { black: '#0A0A0A', white: '#F5F5F0', gold: '#C9A84C', gray: '#888', surface: '#111111' };
const STOREFRONT_URL = import.meta.env.VITE_STOREFRONT_URL || 'https://qrqy.shop';

// TODO(M1): Wire sign out to DELETE /api/auth/session
function useSignOut() {
  const navigate = useNavigate();
  return function signOut() {
    // TODO(M1): Clear session cookie via API call
    navigate('/login');
  };
}

const NAV_ITEMS = [
  { to: '/dashboard', label: 'DASHBOARD', icon: '◈' },
  { to: '/my-qrs',   label: 'MY QRS',    icon: '◼' },
  { to: '/orders',   label: 'ORDERS',    icon: '▣' },
  { to: '/settings', label: 'SETTINGS',  icon: '⊙' },
];

function SidebarNavItem({ to, label, icon }) {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem 1.25rem',
        textDecoration: 'none',
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: '0.15em',
        color: isActive ? COLORS.gold : 'rgba(255,255,255,0.55)',
        backgroundColor: isActive ? 'rgba(201,168,76,0.08)' : 'transparent',
        borderLeft: isActive ? `2px solid ${COLORS.gold}` : '2px solid transparent',
        transition: 'all 0.15s',
      })}
    >
      <span style={{ fontSize: '0.8rem' }}>{icon}</span>
      {label}
    </NavLink>
  );
}

export default function Layout() {
  const signOut = useSignOut();

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: COLORS.black,
      color: COLORS.white,
      fontFamily: 'Inter, system-ui, sans-serif',
      display: 'grid',
      gridTemplateColumns: '220px 1fr',
      gridTemplateRows: 'auto 1fr',
      gridTemplateAreas: `
        "sidebar topbar"
        "sidebar content"
      `,
    }}>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0A0A0A; }
        a { color: inherit; }
        button { cursor: pointer; font-family: inherit; }
      `}</style>

      {/* Sidebar */}
      <aside style={{
        gridArea: 'sidebar',
        backgroundColor: COLORS.surface,
        borderRight: `1px solid rgba(255,255,255,0.06)`,
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 0,
        height: '100vh',
      }}>
        {/* Wordmark */}
        <div style={{
          padding: '1.5rem 1.25rem',
          borderBottom: `1px solid rgba(255,255,255,0.06)`,
        }}>
          <a href={STOREFRONT_URL} style={{ textDecoration: 'none' }}>
            <span style={{
              fontSize: '1.25rem',
              fontWeight: 900,
              letterSpacing: '0.15em',
              color: COLORS.white,
            }}>
              QRQY
            </span>
          </a>
          <p style={{
            fontSize: '0.6rem',
            letterSpacing: '0.15em',
            color: COLORS.gold,
            margin: '0.25rem 0 0',
            fontWeight: 600,
          }}>
            OWNER DASHBOARD
          </p>
        </div>

        {/* Nav links */}
        <nav style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.25rem',
          padding: '1rem 0',
          flex: 1,
        }}>
          {NAV_ITEMS.map((item) => (
            <SidebarNavItem key={item.to} {...item} />
          ))}
        </nav>

        {/* Sign out */}
        <div style={{
          padding: '1rem 1.25rem',
          borderTop: `1px solid rgba(255,255,255,0.06)`,
        }}>
          <button
            onClick={signOut}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.35)',
              fontSize: '0.7rem',
              letterSpacing: '0.15em',
              fontWeight: 600,
              padding: 0,
              width: '100%',
              textAlign: 'left',
              transition: 'color 0.15s',
            }}
          >
            SIGN OUT
          </button>
        </div>
      </aside>

      {/* Top bar */}
      <header style={{
        gridArea: 'topbar',
        height: '56px',
        borderBottom: `1px solid rgba(255,255,255,0.06)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '0 2rem',
        gap: '1.5rem',
        backgroundColor: COLORS.black,
      }}>
        {/* User avatar placeholder */}
        {/* TODO(M1): Replace with real user name + avatar from session */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: COLORS.gold,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.7rem',
            fontWeight: 700,
            color: COLORS.black,
          }}>
            ?
          </div>
          <span style={{
            fontSize: '0.72rem',
            letterSpacing: '0.08em',
            color: 'rgba(255,255,255,0.5)',
          }}>
            {/* TODO(M1): render user.name */}
            —
          </span>
        </div>
      </header>

      {/* Main content */}
      <main style={{
        gridArea: 'content',
        padding: '2.5rem',
        overflowY: 'auto',
        maxWidth: '1100px',
        width: '100%',
      }}>
        <Outlet />
      </main>
    </div>
  );
}
